import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { EntitySheet } from "../../entity-sheet";
import { useNotificationsProvider } from "../../../context/NotificationsContext";
import {
  useInsightContextQuery,
  useSaveInsightContextMutation,
} from "../../../services/api/insightsApi";

/*
 * The tenant context editor.
 *
 * Every tenant operates differently, so the grader is prompted with what this
 * one actually does. Two halves, by writer:
 *
 *  - `operations` and `glossary` are authored here by an admin;
 *  - the "learned" sections below are DERIVED from validate/dismiss feedback on
 *    every run and are read-only. They are shown so an admin can see what the
 *    grader has been taught — and prune it by re-rating the insight, which is
 *    the only way to change them.
 *
 * Uses EntitySheet's read↔edit toggle rather than its own route: this is a
 * settings surface for one page, not a page of its own.
 */

interface ContextSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ContextSheet({ open, onOpenChange }: ContextSheetProps) {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();
  const { data, isFetching } = useInsightContextQuery(undefined, { skip: !open });
  const [saveContext, { isLoading: isSaving }] = useSaveInsightContextMutation();

  const [editing, setEditing] = useState(false);
  const [operations, setOperations] = useState("");

  // Only reset from the server when the sheet opens or the payload arrives —
  // resetting on every render would fight the user's typing.
  useEffect(() => {
    if (data) setOperations(data.operations ?? "");
  }, [data]);

  useEffect(() => {
    if (!open) setEditing(false);
  }, [open]);

  async function handleSave() {
    try {
      await saveContext({ operations }).unwrap();
      showNotification("success", t("insights.context.saved", "Context saved."));
      setEditing(false);
    } catch {
      showNotification("error", t("insights.context.error", "Could not save the context."));
    }
  }

  const learnedNonIssues = data?.knownNonIssues ?? [];
  const learnedFacts = data?.validatedFacts ?? [];

  return (
    <EntitySheet
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      editing={editing}
      onToggleEdit={setEditing}
      canEdit
      title={t("insights.context.title", "Analysis context")}
      description={t(
        "insights.context.description",
        "What this organisation does. Used to make the analysis specific to you.",
      )}
      footer={
        editing ? (
          <div className="flex w-full gap-2">
            <Button className="flex-1" disabled={isSaving} onClick={handleSave}>
              {t("common.save", "Save")}
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              disabled={isSaving}
              onClick={() => {
                setOperations(data?.operations ?? "");
                setEditing(false);
              }}
            >
              {t("common.cancel", "Cancel")}
            </Button>
          </div>
        ) : null
      }
    >
      {isFetching && !data ? (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <div className="space-y-6">
          <section className="space-y-2">
            <Label htmlFor="insights-operations">
              {t("insights.context.operations", "Your operation")}
            </Label>
            {editing ? (
              <Textarea
                id="insights-operations"
                value={operations}
                onChange={(event) => setOperations(event.target.value)}
                rows={10}
                placeholder={t(
                  "insights.context.operations-placeholder",
                  "e.g. Fleet of 40 coaches across two depots. Engines are inspected monthly; brake wear is the most common finding in winter.",
                )}
              />
            ) : (
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                {operations || t("insights.context.empty", "Nothing written yet.")}
              </p>
            )}
          </section>

          <Separator />

          <section className="space-y-2">
            <h3 className="text-sm font-medium">
              {t("insights.context.learned", "Learned from your feedback")}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t(
                "insights.context.learned-hint",
                "Built from insights you confirmed or dismissed. To change these, re-rate the insight.",
              )}
            </p>

            {learnedNonIssues.length === 0 && learnedFacts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("insights.context.learned-empty", "Nothing yet — confirm or dismiss an insight.")}
              </p>
            ) : (
              <div className="space-y-3">
                {learnedNonIssues.length ? (
                  <div>
                    <div className="text-xs font-medium">
                      {t("insights.context.non-issues", "Not a problem here")}
                    </div>
                    <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                      {learnedNonIssues.map((entry: { fingerprint: string; title?: string; reason?: string }) => (
                        <li key={entry.fingerprint}>
                          {entry.title}
                          {entry.reason ? ` — ${entry.reason}` : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {learnedFacts.length ? (
                  <div>
                    <div className="text-xs font-medium">
                      {t("insights.context.confirmed", "Confirmed as real")}
                    </div>
                    <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                      {learnedFacts.map((fact: { source: string; text?: string }) => (
                        <li key={fact.source}>{fact.text}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            )}
          </section>
        </div>
      )}
    </EntitySheet>
  );
}
